<template>
    <section class="seasonality-templates">
        <div v-if="selectedTemplate" class="page-header mt-2">
            <b-button-group class="actions-group mr-1">
                <b-form-select
                    v-if="!isNil(templates)"
                    v-model="selectedTemplate"
                    @change="handleTemplateChange"
                    class="template-select"
                >
                    <option v-for="template in templates" :value="template">{{ template.name }}</option>
                </b-form-select>
                <div class="separator d-lg-none"></div>
                <template v-if="!isTemplateDefault">
                    <b-button variant="default" @click="openEditModal">
                        <font-awesome-icon :icon="['fas', 'pen-to-square']" />
                    </b-button>
                    <b-button v-if="!isTemplateDefault" variant="default" @click="deleteModalOpened = true">
                        <font-awesome-icon :icon="['fas', 'minus']" />
                    </b-button>
                </template>
                <b-button v-if="templates.length <= maxTemplates" variant="default" @click="openImportModal">
                    <font-awesome-icon :icon="['fas', 'arrow-circle-down']" />
                </b-button>
                <b-button v-if="templates.length <= maxTemplates" variant="default" @click="openCloneModal">
                    <font-awesome-icon :icon="['far', 'clone']" class="clone-icon" />
                </b-button>
                <b-button v-if="templates.length <= maxTemplates" variant="default" @click="addModalOpened = true">
                    <font-awesome-icon :icon="['fas', 'plus']" />
                </b-button>
            </b-button-group>

            <b-button variant="default" @click="clonePreviousYearModalOpened = true" class="additional-buttons mr-1"
                >Clone Previous Year</b-button
            >
            <b-button variant="default" @click="syncModalOpened = true" class="additional-buttons mr-1"
                >Sync Properties</b-button
            >

            <p class="mb-0 text-danger" v-if="isTemplateDirty">
                <small>There are template changes pending application.</small>
            </p>

            <modal v-model="editModalOpened" no-close-on-backdrop no-close-on-esc>
                <template #modal-title> Edit Seasonality Template </template>
                <div class="px-3 pb-3">
                    <label class="w-100">
                        <span class="d-inline-block mb-2">Name:</span>
                        <b-form-input
                            v-model="editModalInput"
                            placeholder="Enter seasonality template name"
                        ></b-form-input>
                    </label>
                </div>
                <template #modal-footer>
                    <b-button variant="primary" class="px-3" @click="saveTemplate">Save</b-button>
                    <b-button variant="warning" class="px-3" @click="closeEditModal">Cancel</b-button>
                </template>
            </modal>

            <modal v-model="deleteModalOpened" no-close-on-backdrop no-close-on-esc>
                <template #modal-title> Warning </template>
                <p class="fw-normal">
                    Are you sure you want to delete this template? All associations with properties will be lost.
                </p>
                <template #modal-footer>
                    <b-button variant="primary" class="px-3" @click="removeTemplate">Confirm</b-button>
                    <b-button variant="warning" class="px-3" @click="deleteModalOpened = false">Cancel</b-button>
                </template>
            </modal>

            <modal v-model="importModalOpened" no-close-on-backdrop no-close-on-esc>
                <template #modal-title>
                    Import Template from Property
                </template>
                <b-form-select
                    v-model="importTemplateSelected"
                    :options="importTemplateFromPropertiesOptions"
                    text-field="name"
                    value-field="value"
                    class="mb-3"
                ></b-form-select>
                <template #modal-footer>
                    <b-button variant="primary" class="px-3" @click="importTemplateFromProperty">Import</b-button>
                    <b-button variant="warning" class="px-3" @click="importModalOpened = false">Cancel</b-button>
                </template>
            </modal>

            <modal v-model="cloneModalOpened" no-close-on-backdrop no-close-on-esc>
                <template #modal-title> Clone {{ selectedTemplate.value }} </template>
                <div class="px-3 pb-3">
                    <label class="w-100">
                        <span class="d-inline-block mb-2">New Template Name:</span>
                        <b-form-input
                            v-model="cloneModalInput"
                            placeholder="Enter seasonality template name"
                        ></b-form-input>
                    </label>
                </div>
                <template #modal-footer>
                    <b-button variant="primary" class="px-3" @click="cloneTemplate">Save</b-button>
                    <b-button variant="warning" class="px-3" @click="closeCloneModal">Cancel</b-button>
                </template>
            </modal>
            <modal v-model="addModalOpened" no-close-on-backdrop no-close-on-esc>
                <template #modal-title> Add Seasonality Template </template>
                <div class="px-3 pb-3">
                    <label class="w-100">
                        <span class="d-inline-block mb-2">New Template Name:</span>
                        <b-form-input
                            v-model="addModalInput"
                            placeholder="Enter seasonality template name"
                        ></b-form-input>
                    </label>
                </div>
                <template #modal-footer>
                    <b-button variant="primary" class="px-3" @click="addTemplate" :disabled="!addModalInput"
                        >Save</b-button
                    >
                    <b-button variant="warning" class="px-3" @click="closeAddModal">Cancel</b-button>
                </template>
            </modal>
            <modal v-model="syncModalOpened" no-close-on-backdrop no-close-on-esc>
                <template #modal-title> Sync Properties </template>
                <p>
                    All properties associated with this Seasonality Template will be updated. Do you want to proceed?
                </p>
                <template #modal-footer>
                    <b-button variant="primary" class="px-3" @click="syncProperties">Sync</b-button>
                    <b-button variant="warning" class="px-3" @click="syncModalOpened = false">Cancel</b-button>
                </template>
            </modal>
            <modal v-model="clonePreviousYearModalOpened" no-close-on-backdrop no-close-on-esc>
                <template #modal-title> Warning </template>
                <p>
                    Are you sure want to clone previous year seasons? All of {{ currentYear }} seasons will be deleted!
                </p>
                <template #modal-footer>
                    <b-button variant="warning" class="px-3" @click="clonePreviousYear">Clone</b-button>
                    <b-button variant="default" class="px-3" @click="clonePreviousYearModalOpened = false"
                        >Cancel</b-button
                    >
                </template>
            </modal>
        </div>
    </section>
</template>

<script>
    import { mapGetters } from 'vuex';
    import isNil from 'lodash/isNil';
    import API from '../../../api';
    import Modal from '../../generic/modal/modal.vue';
    import BusyModal from '../../generic/busy-modal/busy-modal.vue';
    import { templatesMixin } from '../mixins/templates-mixin';
    import {
        SEASONALITY_TEMPLATE_TEMPLATE_SYNC_PROPERTIES_ENDPOINT,
        SEASONALITY_TEMPLATE_TEMPLATE_CLONE_ENDPOINT,
        SEASONALITY_TEMPLATE_TEMPLATE_LIST_ENDPOINT,
        SEASONALITY_TEMPLATE_TEMPLATE_DETAIL_ENDPOINT,
        SEASONALITY_TEMPLATE_TEMPLATE_IMPORT_ENDPOINT,
        SEASONALITY_TEMPLATE_TEMPLATE_CLONE_PREVIOUS_YEAR_ENDPOINT,
    } from '../../../endpoints';

    export default {
        name: 'SeasonalityTemplateTemplatesHeader',
        components: {
            Modal,
            BusyModal,
        },
        mixins: [templatesMixin],
        data() {
            return {
                maxTemplates: 20,
                editModalOpened: false,
                editModalInput: null,
                deleteModalOpened: false,
                importModalOpened: false,
                importTemplateSelected: null,
                cloneModalOpened: false,
                cloneModalInput: null,
                addModalOpened: false,
                addModalInput: null,
                syncModalOpened: false,
                cloneYearModalOpened: false,
                clonePreviousYearModalOpened: false,
            };
        },
        computed: {
            isTemplateDefault() {
                return this.selectedTemplate?.default;
            },
            isTemplateDirty() {
                return this.selectedTemplate?.dirty;
            },
        },
        methods: {
            isNil,
            closeAddModal() {
                this.addModalOpened = false;
                this.addModalInput = null;
            },
            openEditModal() {
                this.editModalOpened = true;
                this.editModalInput = this.selectedTemplate.name;
            },
            closeEditModal() {
                this.editModalOpened = false;
                this.editModalInput = null;
            },
            openImportModal() {
                this.importModalOpened = true;
                this.importTemplateSelected = this.importTemplateFromPropertiesOptions[0].value;
            },
            openCloneModal() {
                this.cloneModalOpened = true;
                this.cloneModalInput = `${this.selectedTemplate.name} clone`;
            },
            closeCloneModal() {
                this.cloneModalOpened = false;
                this.cloneModalInput = null;
            },
            handleTemplateChange(value) {
                this.selectedTemplate = value;
            },
            async syncProperties() {
                try {
                    this.syncModalOpened = false;
                    this.busy = true;
                    await API.post(SEASONALITY_TEMPLATE_TEMPLATE_SYNC_PROPERTIES_ENDPOINT(this.selectedTemplate.id));
                    await this.fetchTemplates(true);
                    this.successToast({
                        text: 'Properties successfuly synced.',
                    });
                } catch (e) {
                    this.formattedErrorToast(e, 'Could not sync properties.');
                } finally {
                    this.busy = false;
                }
            },
            async addTemplate() {
                try {
                    this.addModalOpened = false;
                    this.busy = true;
                    const response = await API.post(SEASONALITY_TEMPLATE_TEMPLATE_LIST_ENDPOINT(), {
                        name: this.addModalInput,
                    });
                    await this.fetchTemplates(true);
                    this.selectedTemplate = this.templates.find(t => t.id === response.data.id);
                    this.successToast({
                        text: 'Template successfuly added.',
                    });
                } catch (e) {
                    this.formattedErrorToast(e, 'Could not add template.');
                } finally {
                    this.addModalInput = null;
                    this.busy = false;
                }
            },
            async removeTemplate() {
                try {
                    this.busy = true;
                    this.deleteModalOpened = false;
                    await API.delete(SEASONALITY_TEMPLATE_TEMPLATE_DETAIL_ENDPOINT(this.selectedTemplate.id));

                    this.selectedTemplate = this.templates[0];
                    await this.fetchTemplates(true);

                    this.successToast({
                        text: 'Template successfuly deleted.',
                    });
                } catch (e) {
                    this.formattedErrorToast(e, 'Could not delete template.');
                } finally {
                    this.busy = false;
                }
            },
            async cloneTemplate() {
                try {
                    this.cloneModalOpened = false;
                    this.busy = true;
                    const response = await API.post(
                        SEASONALITY_TEMPLATE_TEMPLATE_CLONE_ENDPOINT(this.selectedTemplate.id),
                        { name: this.cloneModalInput }
                    );
                    await this.fetchTemplates(true);
                    this.selectedTemplate = this.templates.find(t => t.id === response.data.id);
                    this.successToast({
                        text: 'Template successfuly cloned.',
                    });
                } catch (e) {
                    this.formattedErrorToast(e, 'Could not clone template.');
                } finally {
                    this.cloneModalInput = null;
                    this.busy = false;
                }
            },
            async importTemplateFromProperty() {
                try {
                    this.importModalOpened = false;
                    this.busy = true;
                    const response = await API.post(
                        SEASONALITY_TEMPLATE_TEMPLATE_IMPORT_ENDPOINT(this.importTemplateSelected.property_id)
                    );
                    await this.fetchTemplates(true);
                    this.selectedTemplate = this.templates.find(t => t.id === response.data.id);
                    this.successToast({
                        text: 'Template successfuly imported.',
                    });
                } catch (e) {
                    this.formattedErrorToast(e, 'Could not import template.');
                } finally {
                    this.importTemplateSelected = null;
                    this.busy = false;
                }
            },
            async saveTemplate() {
                try {
                    this.editModalOpened = false;
                    this.busy = true;
                    const response = await API.patch(
                        SEASONALITY_TEMPLATE_TEMPLATE_DETAIL_ENDPOINT(this.selectedTemplate.id),
                        { name: this.editModalInput }
                    );
                    await this.fetchTemplates(true);
                    this.selectedTemplate = this.templates.find(t => t.id === response.data.id);
                    this.successToast({
                        text: 'Template successfuly saved.',
                    });
                } catch (e) {
                    this.formattedErrorToast(e, 'Could not import template.');
                } finally {
                    this.editModalInput = null;
                    this.busy = false;
                }
            },
            async clonePreviousYear() {
                try {
                    this.clonePreviousYearModalOpened = false;
                    this.busy = true;
                    const response = await API.post(
                        SEASONALITY_TEMPLATE_TEMPLATE_CLONE_PREVIOUS_YEAR_ENDPOINT(
                            this.selectedTemplate.id,
                            this.currentYear
                        )
                    );
                    await this.fetchSeasonsList(true);
                    await this.fetchSeasonsList();
                    this.successToast({
                        text: 'Previous year successfuly cloned.',
                    });
                } catch (e) {
                    this.formattedErrorToast(e, 'Could not clone previous year.');
                } finally {
                    this.busy = false;
                }
            },
        },
    };
</script>

<style lang="scss" scoped>
    @import '~bootstrap/scss/_functions.scss';
    @import '~bootstrap/scss/_variables.scss';
    @import '~bootstrap/scss/mixins/_breakpoints.scss';

    .seasonality-templates {
        padding: 0 15px;
        .page-header {
            gap: 12px;
            .clone-icon {
                transform: rotate(90deg);
            }
        }
        .seasons-list-grid {
            margin: 0 -15px;
            height: 250px !important;
        }
    }

    :deep(.custom-select:focus) {
        border-color: #ced4da !important;
        outline: none !important;
        box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.075) !important;
    }

    @media (min-width: 960px) {
        :deep(.additional-buttons) {
                min-width: 140px;
            }
            :deep(.template-select) {
                border-top-right-radius: 0 !important;
                border-bottom-right-radius: 0 !important;
            }
        }
        :deep(.show > .btn-default.dropdown-toggle),
        :deep(.btn-default:active) {
            color: #333 !important;
            background-color: #e6e6e6 !important;
            border-color: #adadad !important;
            box-shadow: inset 0 3px 5px rgb(0 0 0 / 13%) !important;
        }
        :deep(.modal-dialog-scrollable) {
            justify-content: center !important;
        }
        @media (max-width: 960px) {
            :deep(.page-header) {
                .actions-group {
                    flex-wrap: wrap;
                    width: 100%;

                    .template-dropdown {
                        width: 100%;
                        > .btn {
                            border-top-right-radius: 0.25rem !important;
                            border-bottom-right-radius: 0.25rem !important;
                        }
                        .dropdown-menu {
                            width: 100%;
                        }
                    }
                    .separator {
                        width: calc(100vw - 40px);
                        padding: 6px;
                    }
                    > .btn:first-of-type {
                        border-top-left-radius: 0.25rem;
                        border-bottom-left-radius: 0.25rem;
                    }
                }
            }
            :deep(.additional-buttons) {
                width: 100%;
                margin-top: 10px;
            }
            :deep(.seasonality-templates) {
                padding: 0 !important;
            }
            :deep(.template-select) {
                border-top-right-radius: 4px;
                border-bottom-right-radius: 4px;
            }
        }
</style>
